import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Grid,
  Link,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DownloadIcon from "@mui/icons-material/Download";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";

// question sets
import {
  RENEWAL_FORM_QUESTIONS_CASE_A,
  RENEWAL_FORM_QUESTIONS_CASE_B,
  RENEWAL_FORM_QUESTIONS_CASE_C,
  RENEWAL_FORM_QUESTIONS_CASE_D,
  RENEWAL_FORM_QUESTIONS_CASE_E,
} from "@modules/application-renewal/constants/staticRenewalAccessData";
import { fetchRenewalRequestThunk } from "@modules/application-renewal/redux/thunk/renewalThunk";
import {
  getActualFileName,
  openFileBySignedUrl,
} from "../../../../src/utils/getViewSignedUrlHandler";
import { getFileSignedURLThunk as getFileSignedROW } from "@modules/stepper-row/redux/thunk/documentThunk";
import { getFileSignedURLThunk as getFileSignedAccess } from "@modules/stepper/redux/thunk/documentThunk";

interface Props {
  isExpanded: boolean;
  toggleExpand: () => void;
  applicationId: string | number;
  access: boolean;
  category: string;
}

// -------- Helpers --------
const formatDate = (dateString?: string) => {
  if (!dateString) return "NA";
  return dayjs(dateString).format("DD MMM YYYY");
};

const BASE_URL = "";

export const RenewalDetailsSection: React.FC<Props> = ({
  isExpanded,
  toggleExpand,
  applicationId,
  access,
  category,
}) => {
  const dispatch = useDispatch<any>();

  const [renewalData, setRenewalData] = useState<any>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [documents, setDocuments] = useState<any[]>([]);
  const [photos, setPhotos] = useState<any[]>([]);

  // ✅ ACTIVE QUESTIONS (same as your logic)
  const ACTIVE_QUESTIONS = access
    ? category?.includes("Fuel")
      ? RENEWAL_FORM_QUESTIONS_CASE_A
      : RENEWAL_FORM_QUESTIONS_CASE_B
    : category?.includes("Industrial")
      ? RENEWAL_FORM_QUESTIONS_CASE_C
      : category?.includes("BharatNet")
        ? RENEWAL_FORM_QUESTIONS_CASE_E
        : RENEWAL_FORM_QUESTIONS_CASE_D;

  // ✅ YOUR FUNCTION INSIDE COMPONENT
  const fetchExistingRenewalData = async () => {
    try {
      if (!applicationId) return;

      const res = await dispatch(
        fetchRenewalRequestThunk(applicationId),
      ).unwrap();

      const data = res?.data;
      if (!data || !data?.id) return;

      setRenewalData(data);

      // 🔥 Map IDs → keys
      const answersMap: Record<string, string> = {};

      const trueIds = data.questions_true_renewal || [];
      const falseIds = data.questions_false_renewal || [];

      ACTIVE_QUESTIONS.forEach((q) => {
        if (q.type === "radio") {
          if (trueIds.includes(q.id)) {
            answersMap[q.key] = "yes";
          } else if (falseIds.includes(q.id)) {
            answersMap[q.key] = "no";
          }
        }
      });

      setAnswers(answersMap);

      // ✅ Split docs
      const docs = data.renewal_documents || [];

      const docsOnly = docs.filter((doc: any) =>
        doc.fileName.match(/\.(pdf|doc|docx)$/i),
      );

      const photosOnly = docs.filter((doc: any) =>
        doc.fileName.match(/\.(png|jpg|jpeg)$/i),
      );

      setDocuments(docsOnly);
      setPhotos(photosOnly);
    } catch (err) {
      console.error(err);
    }
  };

  const photoContent = access
    ? {
        title: "Access Related Photographs",
        points: [
          "Photograph showing the site along with the National Highway",
          "Photograph of access (entry/exit) and visibility, including any obstruction",
        ],
      }
    : {
        title: "Supporting Photographs",
        points: [
          "Location of utility within ROW",
          "Alignment/placement and road condition",
          "Impact on safety (drainage, structures, traffic)",
          "Any deviation, violation, or encroachment",
        ],
      };

  const handleViewFile = (doc_name: any) => {
    openFileBySignedUrl({
      document_name: doc_name,
      dispatch,
      getSignedUrlThunk: access ? getFileSignedAccess : getFileSignedROW,
    });
  };

  useEffect(() => {
    fetchExistingRenewalData();
  }, [applicationId]);

  const questionsArr = ACTIVE_QUESTIONS.filter((q) => q.type === "radio")
    .map((q) => {
      const val = answers[q.key];
      if (!val) return null;

      return {
        question_name: q.label,
        value: val === "yes" ? "Yes" : "No",
      };
    })
    .filter(Boolean);

  const pairQuestions = (arr: any[]) => {
    const pairs = [];
    for (let i = 0; i < arr.length; i += 2) {
      pairs.push([arr[i], arr[i + 1] || null]);
    }
    return pairs;
  };

  const questionPairs = pairQuestions(questionsArr);

  if (!renewalData?.renewal_applied_at) return null;

  return (
    <Box>
      <Card sx={{ border: "1px solid #E7E8E9", boxShadow: "none" }}>
        {/* Header */}
        <Box
          sx={{
            backgroundColor: "#F4F2F7",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pl: 2,
            py: 1,
          }}
        >
          <Typography fontSize={14} fontWeight={700}>
            Renewal Application Details
          </Typography>

          <IconButton onClick={toggleExpand}>
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>

        {/* Content */}
        {isExpanded && renewalData && (
          <CardContent>
            {/* 🔹 Top Info */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography className="preview-dropdown-label">
                  Brief Description of Renewal
                </Typography>
                <Typography className="preview-dropdown-value">
                  {renewalData?.renewal_description || "NA"}
                </Typography>
              </Grid>
            </Grid>

            {/* 🔹 Questions */}
            <Box mt={2}>
              {questionPairs.map((pair, idx) => (
                <Grid container spacing={2} key={idx}>
                  {pair[0] && (
                    <Grid item xs={12} md={6}>
                      <Typography className="preview-dropdown-label" my={1}>
                        {pair[0].question_name}
                      </Typography>
                      <Typography className="preview-dropdown-value">
                        {pair[0].value}
                      </Typography>
                    </Grid>
                  )}

                  {pair[1] && (
                    <Grid item xs={12} md={6}>
                      <Typography className="preview-dropdown-label" my={1}>
                        {pair[1].question_name}
                      </Typography>
                      <Typography className="preview-dropdown-value">
                        {pair[1].value}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              ))}
            </Box>
            <Grid item xs={12}>
              {/* 🔹 Section Title */}
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#111827",
                  my: 1,
                }}
              >
                Related Documents
              </Typography>
              <Box
                sx={{
                  background: "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  borderRadius: "10px",
                  px: 2,
                  py: 1.5,
                  mb: 2,
                }}
              >
                <Typography fontSize={12} color="text.secondary" mt={0.5}>
                  <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                    <li>
                      <strong>
                        Compliance certificate / self-certification - Mandatory
                        if NH Updated 2L to 4L.
                      </strong>
                      <br />
                      (Applicant's declaration that the outlet is compliant with
                      current access permission norms. Where NH has been
                      upgraded (2L to 4L), a certificate from proposing
                      authority that service road has been built)
                    </li>

                    <li style={{ marginTop: "8px" }}>
                      <strong>
                        Proof of NH status change (if applicable) - Optional
                      </strong>
                      <br />
                      Documentary evidence if NH has been upgraded from 2-lane
                      to 4-lane since original permission.
                      <br />
                      Triggers mandatory service road construction at renewal —
                      at applicant's own cost.
                    </li>
                  </ul>
                </Typography>
              </Box>

              {/* 🔹 Files List */}
              <Box display="flex" flexDirection="column" gap={1} mb={2}>
                {documents?.length ? (
                  documents.map((doc: any, i: number) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        border: "1px solid #E5E7EB",
                        borderRadius: "10px",
                        px: 2,
                        py: 1.2,
                        backgroundColor: "#FFFFFF",
                        transition: "all 0.2s ease",
                        cursor: "pointer",

                        "&:hover": {
                          borderColor: "#D1D5DB",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                          backgroundColor: "#FAFAFA",
                        },
                      }}
                      onClick={() => handleViewFile(doc?.fileName)}
                    >
                      {/* 🔹 Left: Icon + Name */}
                      <Box display="flex" alignItems="center" gap={1.2}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "6px",
                            background: "#F3F4F6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 14,
                          }}
                        >
                          <DescriptionIcon
                            sx={{ color: "#6B7280", fontSize: 16 }}
                          />
                        </Box>

                        <Typography
                          sx={{
                            fontSize: 13,
                            color: "#111827",
                            maxWidth: "260px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {getActualFileName(
                            doc.originalFileName || doc.fileName,
                          )}
                        </Typography>
                      </Box>

                      {/* 🔹 Right: Action */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          color: "#3A1C73",
                          fontSize: 13,
                          fontWeight: 500,
                        }}
                      >
                        <DownloadIcon sx={{ fontSize: 16 }} />
                        View
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                    No documents available
                  </Typography>
                )}
              </Box>
            </Grid>
            <Box>
              {/* 🔹 Title */}
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#111827",
                  my: 1,
                }}
              >
                {photoContent.title}
              </Typography>

              {/* 🔹 Instruction Card */}
              <Box
                sx={{
                  background: "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  borderRadius: "10px",
                  px: 2,
                  py: 1.5,
                  mb: 2,
                }}
              >
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {photoContent.points.map((point: string, index: number) => (
                    <Typography
                      component="li"
                      key={index}
                      sx={{
                        fontSize: 12.5,
                        color: "#374151",
                        mb: 0.4,
                        lineHeight: 1.4,
                      }}
                    >
                      {point}
                    </Typography>
                  ))}
                </Box>
              </Box>

              {/* 🔹 Files Section */}
              <Box display="flex" flexDirection="column" gap={1.2}>
                {photos?.length ? (
                  photos.map((doc: any, i: number) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        border: "1px solid #E5E7EB",
                        borderRadius: "10px",
                        px: 2,
                        py: 1.2,
                        backgroundColor: "#FFFFFF",
                        transition: "all 0.2s ease",
                        cursor: "pointer",

                        "&:hover": {
                          borderColor: "#D1D5DB",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                          backgroundColor: "#FAFAFA",
                        },
                      }}
                      onClick={() => handleViewFile(doc?.fileName)}
                    >
                      {/* 🔹 Left: File Info */}
                      <Box display="flex" alignItems="center" gap={1.2}>
                        {/* Icon */}
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "6px",
                            background: "#EEF2FF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 14,
                          }}
                        >
                          <ImageIcon sx={{ color: "#16663a", fontSize: 16 }} />
                        </Box>

                        {/* File Name */}
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: "#111827",
                            maxWidth: "220px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {getActualFileName(
                            doc.originalFileName || doc.fileName,
                          )}
                        </Typography>
                      </Box>

                      {/* 🔹 Right: Action */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          color: "#3a1c73",
                          fontSize: 13,
                          fontWeight: 500,
                        }}
                      >
                        <DownloadIcon sx={{ fontSize: 16 }} />
                        View
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                    No photos available
                  </Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        )}
      </Card>
    </Box>
  );
};
